(function () {
  'use strict';

  const CONFIG = {
    MY_GARAGE_PATHS: ['/my-garage', '/bike-profile', '/my-garage-dashboard'],
    FITMENTS_PATH: '/fitments',
    CART_SOURCE: 'my-garage',
    DEBUG: false
  };

  const state = {
    initialized: false,
    currentBikeModel: null,
    isMyGaragePage: false,
    isBikeProfilePage: false,
    isMyGarageDashboardPage: false,
    isFitmentsPage: false,
    observer: null
  };

  const utils = {
    log: (...args) => {
      if (CONFIG.DEBUG || window.location.search.includes('debug_analytics')) {
        console.log('[My Garage Analytics]', ...args);
      }
    },

    isMyGaragePage: () => {
      return CONFIG.MY_GARAGE_PATHS.some(path =>
        window.location.pathname.includes(path)
      );
    },

    isSpecificMyGaragePage: () => {
      return window.location.pathname.includes('/my-garage');
    },

    isBikeProfilePage: () => {
      return window.location.pathname.includes('/bike-profile');
    },

    isMyGarageDashboardPage: () => {
      return window.location.pathname.includes('/my-garage-dashboard');
    },

    isFitmentsPage: () => {
      return window.location.pathname.includes(CONFIG.FITMENTS_PATH);
    },

    getCurrentPageType: () => {
      if (utils.isSpecificMyGaragePage()) return 'my-garage';
      if (utils.isBikeProfilePage()) return 'bike-profile';
      if (utils.isMyGarageDashboardPage()) return 'my-garage-dashboard';
      if (utils.isFitmentsPage()) return 'fitments';
      return 'other';
    },

    getBikeModel: (element) => {
      if (!element) return null;

      const bikeCard = element.closest('.bike-card');
      if (bikeCard) {
        const bikeName = bikeCard.querySelector('.bike-name a');
        return bikeName ? bikeName.textContent.trim() : null;
      }
      if (element.dataset && element.dataset.bikeName) {
        return element.dataset.bikeName;
      }

      return null;
    },

    getBikeModelFromUrl: () => {
      const pathParts = window.location.pathname.split('/');

      const fitmentsIndex = pathParts.indexOf('fitments');
      if (fitmentsIndex !== -1 && pathParts[fitmentsIndex + 1]) {
        return pathParts[fitmentsIndex + 1];
      }

      return null;
    },

    getBikeNameFromDOM: async (maxWait = 5000) => {
      const bikeNameElement = document.querySelector('.bike-name');

      if (!bikeNameElement) return null;

      const currentText = bikeNameElement.textContent.trim();
      if (currentText && currentText !== 'Loading your bike...') {
        return currentText;
      }

      return new Promise((resolve) => {
        const startTime = Date.now();

        const checkBikeName = () => {
          const text = bikeNameElement.textContent.trim();

          if (text && text !== 'Loading your bike...') {
            resolve(text);
            return;
          }

          if (Date.now() - startTime > maxWait) {
            resolve(null);
            return;
          }

          requestAnimationFrame(checkBikeName);
        };

        checkBikeName();
      });
    },

    isGtagAvailable: () => {
      return typeof gtag === 'function';
    },


    fireGtagEvent: (eventName, eventParams = {}) => {
      if (!utils.isGtagAvailable()) {
        utils.log('gtag not available, event not fired:', eventName, eventParams);
        return false;
      }

      try {
        gtag('event', eventName, eventParams);
        utils.log('GA4 Event Fired:', eventName, eventParams);
        return true;
      } catch (error) {
        console.error('Error firing GA4 event:', error);
        return false;
      }
    },

    dispatchCustomEvent: (eventName, detail = {}) => {
      try {
        const event = new CustomEvent(eventName, {
          detail,
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(event);
        utils.log('Custom Event Dispatched:', eventName, detail);
        return true;
      } catch (error) {
        console.error('Error dispatching custom event:', error);
        return false;
      }
    },

    getProductData: (element) => {
      const productCard = element.closest('.product-card, [data-product-id]');

      return {
        product_id: productCard?.dataset?.productId || null,
        variant_id: productCard?.dataset?.variantId || null,
        product_name: productCard?.querySelector('.product-title, .card__heading')?.textContent?.trim() || null
      };
    },

    waitForElement: (selector, timeout = 5000) => {
      return new Promise((resolve) => {
        if (document.querySelector(selector)) {
          return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
          const element = document.querySelector(selector);
          if (element) {
            observer.disconnect();
            resolve(element);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        setTimeout(() => {
          observer.disconnect();
          resolve(null);
        }, timeout);
      });
    }
  };

  const tracking = {
    trackPageView: () => {
      if (!state.isMyGaragePage) return;

      const pageType = utils.getCurrentPageType();
      const eventName = pageType === 'my-garage' ? 'my_garage_page_view' :
        pageType === 'bike-profile' ? 'bike_profile_page_view' :
          pageType === 'my-garage-dashboard' ? 'my_garage_dashboard_page_view' :
            'my_garage_page_view';

      utils.fireGtagEvent(eventName, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_type: pageType
      });
    },


    trackBikeSelected: (bikeModel) => {
      if (!bikeModel) return;

      state.currentBikeModel = bikeModel;

      const pageType = utils.getCurrentPageType();

      utils.fireGtagEvent('my_garage_bike_selected', {
        bike_model: bikeModel,
        page_location: window.location.href,
        page_type: pageType,
        source_page: pageType
      });

      utils.dispatchCustomEvent('garage:bikeSelected', {
        bike_model: bikeModel,
        page_type: pageType
      });

      try {
        sessionStorage.setItem('my_garage_selected_bike', bikeModel);
      } catch (e) {
        utils.log('Error storing bike in sessionStorage:', e);
      }
    },

    trackAddToCart: (productData, bikeModel) => {
      if (!productData.product_id && !productData.product_name) {
        utils.log('Insufficient product data for tracking:', productData);
        return;
      }

      const pageType = utils.getCurrentPageType();

      const eventData = {
        bike_model: bikeModel || state.currentBikeModel,
        source: CONFIG.CART_SOURCE,
        page_type: pageType,
        source_page: pageType,
        ...productData
      };

      utils.fireGtagEvent('my_garage_add_to_cart', eventData);
      utils.dispatchCustomEvent('garage:addToCart', eventData);
    }
  };


  const cartAttribution = {
    injectCartData: (bodyData, bikeModel) => {
      const params = new URLSearchParams(window.location.search);
      const hasContext = params.get('parts-source');

      if (!bikeModel) {
        bikeModel = state.currentBikeModel ||
          sessionStorage.getItem('my_garage_selected_bike');
      }

      if (!bikeModel) return bodyData;

      
      if (hasContext === 'my-garage') {
        if (bodyData.items && Array.isArray(bodyData.items)) {

          bodyData.items = bodyData.items.map(item => ({
            ...item,
            properties: {
              ...(item.properties || {}),
              _mygarage_bike_model: bikeModel,
              Fits: bikeModel,
              _source: CONFIG.CART_SOURCE
            }
          }));
        } else if (bodyData.id || bodyData.variant_id) {
          bodyData.properties = {
            ...(bodyData.properties || {}),
            _mygarage_bike_model: bikeModel,
            Fits: bikeModel,
            _source: CONFIG.CART_SOURCE
          };
        }

        utils.log('Injected line item properties:', {
          bike_model: bikeModel,
          Fits: bikeModel,
          source: CONFIG.CART_SOURCE,
          data: bodyData
        });
      }

      return bodyData;
    },


    addCartAttributes: async (bikeModel) => {
      utils.log('Cart attributes deprecated - using line item properties instead');
      return true;
    }
  };


  const eventListeners = {
    handleBikeAction: (event) => {
      const target = event.target.closest('a.action-btn, .bike-name a');
      if (!target) return;

      const bikeModel = utils.getBikeModel(target);
      if (bikeModel) {
        tracking.trackBikeSelected(bikeModel);
      }
    },


    handleBikeSearchSelect: (event) => {
      const target = event.target;

      if (target.closest('.modal-bike-predictive-search-results-list')) {
        const bikeSearchInput = document.querySelector('.bike-search-input');
        if (bikeSearchInput && bikeSearchInput.value) {
          setTimeout(() => {
            const selectedBike = bikeSearchInput.value.trim();
            if (selectedBike) {
              tracking.trackBikeSelected(selectedBike);
            }
          }, 100);
        }
      }
    },

    handleAddToCart: (event) => {
      const target = event.target.closest('button[name="add"], .product-form__submit, [type="submit"]');
      if (!target) return;

      const bikeModel = state.currentBikeModel ||
        sessionStorage.getItem('my_garage_selected_bike');

      if (!bikeModel) return;

      const productData = utils.getProductData(target);

      tracking.trackAddToCart(productData, bikeModel);
    },

    interceptFetch: () => {
      const originalFetch = window.fetch;

      window.fetch = async function (...args) {
        const [url, options] = args;

        if (url && typeof url === 'string' && url.includes('/cart/add')) {

          const bikeModel = state.currentBikeModel ||
            sessionStorage.getItem('my_garage_selected_bike');

          if (bikeModel && options && options.body) {
            try {
              const body = JSON.parse(options.body);
              const updatedBody = cartAttribution.injectCartData(body, bikeModel);
              options.body = JSON.stringify(updatedBody);

              utils.log('Intercepted cart add request, injected data:', updatedBody);
            } catch (e) {
              utils.log('Error intercepting fetch:', e);
            }
          }
        }

        return originalFetch.apply(this, args);
      };
    },


    setup: () => {
      document.addEventListener('click', eventListeners.handleBikeAction, true);
      document.addEventListener('click', eventListeners.handleBikeSearchSelect, true);
      document.addEventListener('click', eventListeners.handleAddToCart, true);

      eventListeners.interceptFetch();

      utils.log('Event listeners setup complete');
    }
  };

  const initializePageTracking = async () => {
    state.isMyGaragePage = utils.isMyGaragePage();
    state.isBikeProfilePage = utils.isBikeProfilePage();
    state.isMyGarageDashboardPage = utils.isMyGarageDashboardPage();
    state.isFitmentsPage = utils.isFitmentsPage();

    const pageType = utils.getCurrentPageType();

    if (state.isMyGaragePage) {
      tracking.trackPageView();
      utils.log(`Initialized ${pageType} page tracking`);
    }

    if (state.isFitmentsPage) {
      const bikeModel = utils.getBikeModelFromUrl();
      if (bikeModel) {
        state.currentBikeModel = bikeModel;
        utils.log(`${pageType} page bike model from URL:`, bikeModel);
      }
    }

    if (state.isBikeProfilePage) {
      utils.log('Bike profile page detected, waiting for bike name...');
      const bikeName = await utils.getBikeNameFromDOM();

      if (bikeName) {
        state.currentBikeModel = bikeName;
        utils.log('Bike profile page bike name from DOM:', bikeName);

        tracking.trackBikeSelected(bikeName);
      } else {
        const bikeIdFromUrl = window.location.pathname.split('/').pop();
        if (bikeIdFromUrl) {
          state.currentBikeModel = bikeIdFromUrl;
          utils.log('Fallback: Using bike ID from URL:', bikeIdFromUrl);
        }
      }
    }

    try {
      const storedBike = sessionStorage.getItem('my_garage_selected_bike');
      if (storedBike && !state.currentBikeModel) {
        state.currentBikeModel = storedBike;
        utils.log('Restored bike model from session:', storedBike);
      }
    } catch (e) {
      utils.log('Error accessing sessionStorage:', e);
    }
  };

  const init = async () => {
    if (state.initialized) return;

    utils.log('Initializing My Garage Analytics...');

    await initializePageTracking();

    eventListeners.setup();

    state.initialized = true;
    utils.log('My Garage Analytics initialized successfully');
  };

  window.MyGarageAnalytics = {
    trackBikeSelected: tracking.trackBikeSelected,
    trackAddToCart: tracking.trackAddToCart,
    addCartAttributes: cartAttribution.addCartAttributes,
    getCurrentBike: () => state.currentBikeModel,
    utils: utils
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
